import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { PlusIcon, UserGroupIcon, PencilIcon, TrashIcon, MagnifyingGlassIcon, CheckIcon, XMarkIcon, ExclamationTriangleIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { usePersistListFiltersSession } from '../../hooks/usePersistListFiltersSession';
import { LIST_FILTER_SESSION_KEYS } from '../../constants/listFilterSessionKeys';

import { useUserPermissions } from '../../hooks/useUserPermissions';
import { toast } from 'react-hot-toast';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import TableColumnFilter from '../../components/TableColumnFilter';
import ActiveFilterChips, { FilterChip } from '../../components/ActiveFilterChips';
import {
  formatGroupWorkLocation,
  getGroupWorkLocationBadgeClass,
  type GroupWorkLocation,
} from '../../constants/groupWorkLocation';

interface Group {
  id: string;
  name: string;
  description?: string;
  type: string;
  groupType: string;
  email?: string;
  isActive: boolean;
  workLocation?: GroupWorkLocation;
  defaultHourlyRate: number;
  manager?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  parent?: {
    id: string;
    name: string;
  };
  members?: Array<{
    id: string;
    userId: string;
    role: string;
    isActive: boolean;
    user: {
      id: string;
      firstName: string;
      lastName: string;
      email: string;
    };
  }>;
  _count?: {
    members: number;
  };
}

interface GroupAssignments {
  groupId: string;
  groupName: string;
  hasAssignments: boolean;
  totalAssignments: number;
  tickets: {
    incidents: number;
    problems: number;
    changes: number;
    serviceRequests: number;
    cases: number;
  };
  engagements: {
    count: number;
  };
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const TYPE_FILTER_OPTIONS = [
  { value: 'assignment', label: 'Assignment' },
  { value: 'approval', label: 'Approval' },
  { value: 'notification', label: 'Notification' },
  { value: 'peer_review', label: 'Peer Review' },
  { value: 'customer_approver', label: 'Customer Approver' },
  { value: 'assessment', label: 'Assessment' },
  { value: 'billing', label: 'Billing' },
];

const ACTIVE_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

export default function GroupsList() {
  const { canViewEntity, canCreateEntity, canUpdateEntity, canDeleteEntity, loading: permissionsLoading } = useUserPermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  usePersistListFiltersSession('/groups', LIST_FILTER_SESSION_KEYS.groups);

  const patchSearchParams = useCallback(
    (updates: Record<string, string | null | undefined>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, val] of Object.entries(updates)) {
            if (val === null || val === undefined || val === '') {
              next.delete(key);
            } else {
              next.set(key, val);
            }
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const page = useMemo(() => {
    const p = parseInt(searchParams.get('page') || '1', 10);
    return Number.isFinite(p) && p >= 1 ? p : 1;
  }, [searchParams]);

  const pageSize = useMemo(() => {
    const ps = parseInt(searchParams.get('pageSize') || '10', 10);
    return PAGE_SIZE_OPTIONS.includes(ps) ? ps : 10;
  }, [searchParams]);

  const searchTerm = searchParams.get('search') ?? '';
  const filterType = searchParams.get('type') ?? '';
  const filterName = searchParams.get('name') ?? '';
  const filterManager = searchParams.get('manager') ?? '';
  const filterActive = searchParams.get('active') ?? 'all';

  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') ?? '');
  useEffect(() => {
    setSearchInput(searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    const id = setTimeout(() => {
      const trimmed = searchInput.trim();
      const current = searchParams.get('search') ?? '';
      if (trimmed === current) return;
      patchSearchParams({ search: trimmed || null, page: '1' });
    }, 300);
    return () => clearTimeout(id);
  }, [searchInput, searchParams, patchSearchParams]);

  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);

  // Check if user can access groups
  const canAccessGroups = canViewEntity('groups');
  const canCreateGroups = canCreateEntity('groups');
  const canEditGroups = canUpdateEntity('groups');
  const canDeleteGroups = canDeleteEntity('groups');
  const [editingRateId, setEditingRateId] = useState<string | null>(null);
  const [editingRateValue, setEditingRateValue] = useState<string>('');
  const [savingRateId, setSavingRateId] = useState<string | null>(null);

  // Reassignment modal state
  const [reassignModalOpen, setReassignModalOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<{ id: string; name: string } | null>(null);
  const [assignments, setAssignments] = useState<GroupAssignments | null>(null);
  const [targetGroupId, setTargetGroupId] = useState<string>('');
  const [assignedToId, setAssignedToId] = useState<string>('');
  const [targetGroupMembers, setTargetGroupMembers] = useState<Array<{ id: string; firstName: string; lastName: string; email: string }>>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [reassigning, setReassigning] = useState(false);
  const [checkingAssignments, setCheckingAssignments] = useState(false);

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType) params.append('type', filterType);
      if (filterActive === 'inactive') {
        params.append('active', 'inactive');
      } else if (filterActive === 'all') {
        params.append('active', 'all');
      }

      const response = await api.get(`/groups?limit=1000&${params.toString()}`);
      setGroups(response.data.groups || response.data);
    } catch (error) {
      console.error('Error fetching groups:', error);
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, [filterType, filterActive]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const renderColumnHeader = useCallback(
    (columnKey: string, columnLabel: string) => {
      switch (columnKey) {
        case 'name':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterName}
              placeholder="Filter by name..."
              onChange={(v) => {
                patchSearchParams({ name: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'type':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="single-select"
              options={TYPE_FILTER_OPTIONS}
              value={filterType}
              placeholder="All Types"
              onChange={(v) => {
                patchSearchParams({ type: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'manager':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterManager}
              placeholder="Filter by manager..."
              onChange={(v) => {
                patchSearchParams({ manager: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'status':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="single-select"
              options={ACTIVE_FILTER_OPTIONS}
              value={filterActive}
              placeholder="All Statuses"
              active={filterActive !== 'all'}
              onChange={(v) => {
                const val = typeof v === 'string' ? v : 'all';
                patchSearchParams({ active: val === 'all' ? null : val, page: '1' });
              }}
            />
          );
        default:
          return columnLabel;
      }
    },
    [filterName, filterType, filterManager, filterActive, patchSearchParams]
  );

  const activeFilterChips: FilterChip[] = useMemo(() => {
    const chips: FilterChip[] = [];
    if (searchTerm) {
      chips.push({ id: 'search', label: 'Search', value: `"${searchTerm}"`, color: 'blue' });
    }
    if (filterName) {
      chips.push({ id: 'name', label: 'Name', value: filterName, color: 'blue' });
    }
    if (filterType) {
      const label = TYPE_FILTER_OPTIONS.find((o) => o.value === filterType)?.label || filterType;
      chips.push({ id: 'type', label: 'Type', value: label, color: 'purple' });
    }
    if (filterManager) {
      chips.push({ id: 'manager', label: 'Manager', value: filterManager, color: 'teal' });
    }
    if (filterActive !== 'all') {
      chips.push({
        id: 'active',
        label: 'Status',
        value: filterActive === 'active' ? 'Active' : 'Inactive',
        color: 'green',
      });
    }
    return chips;
  }, [searchTerm, filterName, filterType, filterManager, filterActive]);

  const handleRemoveFilterChip = (chipId: string) => {
    switch (chipId) {
      case 'search':
        setSearchInput('');
        patchSearchParams({ search: null, page: '1' });
        break;
      case 'name':
        patchSearchParams({ name: null, page: '1' });
        break;
      case 'type':
        patchSearchParams({ type: null, page: '1' });
        break;
      case 'manager':
        patchSearchParams({ manager: null, page: '1' });
        break;
      case 'active':
        patchSearchParams({ active: null, page: '1' });
        break;
    }
  };

  const handleClearAllFilters = () => {
    setSearchInput('');
    patchSearchParams({
      search: null,
      name: null,
      type: null,
      manager: null,
      active: null,
      page: '1',
    });
  };

  const filteredGroups = useMemo(() => {
    let result = groups;

    if (searchTerm.trim()) {
      const q = searchTerm.trim().toLowerCase();
      result = result.filter(
        (group) =>
          group.name.toLowerCase().includes(q) ||
          group.description?.toLowerCase().includes(q) ||
          group.email?.toLowerCase().includes(q)
      );
    }

    if (filterName.trim()) {
      const q = filterName.trim().toLowerCase();
      result = result.filter(
        (group) =>
          group.name.toLowerCase().includes(q) ||
          group.description?.toLowerCase().includes(q)
      );
    }

    if (filterManager.trim()) {
      const q = filterManager.trim().toLowerCase();
      result = result.filter((group) => {
        if (!group.manager) return false;
        const fullName = `${group.manager.firstName} ${group.manager.lastName}`.toLowerCase();
        return fullName.includes(q) || group.manager.email.toLowerCase().includes(q);
      });
    }

    return result;
  }, [groups, searchTerm, filterName, filterManager]);

  const totalPages = Math.max(1, Math.ceil(filteredGroups.length / pageSize) || 1);

  useEffect(() => {
    if (page > totalPages) {
      patchSearchParams({ page: String(totalPages) });
    }
  }, [filteredGroups.length, page, totalPages, patchSearchParams]);

  const paginatedGroups = useMemo(
    () => filteredGroups.slice((page - 1) * pageSize, page * pageSize),
    [filteredGroups, page, pageSize]
  );

  const handleStartEditRate = (groupId: string, currentRate: number) => {
    setEditingRateId(groupId);
    setEditingRateValue(currentRate.toString());
  };

  const handleCancelEditRate = () => {
    setEditingRateId(null);
    setEditingRateValue('');
  };

  const handleSaveRate = async (groupId: string) => {
    const newRate = parseFloat(editingRateValue);

    if (isNaN(newRate) || newRate < 0) {
      toast.error('Please enter a valid hourly rate');
      return;
    }

    setSavingRateId(groupId);
    try {
      await api.put(`/groups/${groupId}`, {
        defaultHourlyRate: newRate
      });

      // Update the local state
      setGroups(prevGroups =>
        prevGroups.map(group =>
          group.id === groupId
            ? { ...group, defaultHourlyRate: newRate }
            : group
        )
      );

      setEditingRateId(null);
      setEditingRateValue('');
      toast.success('Hourly rate updated successfully');
    } catch (error: any) {
      console.error('Error updating hourly rate:', error);
      toast.error(error.response?.data?.message || 'Failed to update hourly rate');
    } finally {
      setSavingRateId(null);
    }
  };

  const handleDelete = async (id: string, groupName: string) => {
    setCheckingAssignments(true);
    setGroupToDelete({ id, name: groupName });

    try {
      // Check if group has active assignments
      const response = await api.get(`/groups/${id}/assignments`);
      const assignmentData: GroupAssignments = response.data;

      if (assignmentData.hasAssignments) {
        // Show reassignment modal
        setAssignments(assignmentData);
        setReassignModalOpen(true);
      } else {
        // No assignments, proceed with simple delete confirmation
        showDeleteConfirmation(id, groupName);
      }
    } catch (error) {
      console.error('Error checking group assignments:', error);
      toast.error('Failed to check group assignments');
      setGroupToDelete(null);
    } finally {
      setCheckingAssignments(false);
    }
  };

  const showDeleteConfirmation = (id: string, groupName: string) => {
    confirmAlert({
      title: 'Confirm Delete',
      message: `Are you sure you want to delete group "${groupName}"? This action cannot be undone.`,
      buttons: [],
      closeOnEscape: true,
      closeOnClickOutside: true,
      overlayClassName: 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50',
      customUI: ({ onClose, title, message }) => (
        <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md mx-4 border border-gray-100">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.99-.833-2.76 0L3.054 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
            </div>
            <div className="ml-4 flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{message}</p>
            </div>
          </div>
          <div className="mt-8 flex flex-col-reverse sm:flex-row sm:justify-end space-y-2 space-y-reverse sm:space-y-0 sm:space-x-3">
            <button
              className="px-6 py-3 bg-gray-200 text-gray-900 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200 font-medium"
              onClick={() => {
                onClose();
                setGroupToDelete(null);
              }}
            >
              Cancel
            </button>
            <button
              className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all duration-200 font-medium"
              onClick={async () => {
                try {
                  await api.delete(`/groups/${id}`);
                  onClose();
                  setGroupToDelete(null);
                  fetchGroups();
                  toast.success('Group deleted successfully');
                } catch (error) {
                  console.error('Error deleting group:', error);
                  toast.error('Failed to delete group');
                }
              }}
            >
              Delete
            </button>
          </div>
        </div>
      )
    });
  };

  const fetchGroupMembers = async (groupId: string) => {
    setLoadingMembers(true);
    setAssignedToId('');
    try {
      const response = await api.get(`/groups/${groupId}`);
      const members = response.data.members || [];
      // Extract user info from active members
      const activeMembers = members
        .filter((m: any) => m.isActive && m.user)
        .map((m: any) => ({
          id: m.user.id,
          firstName: m.user.firstName,
          lastName: m.user.lastName,
          email: m.user.email
        }));
      setTargetGroupMembers(activeMembers);
    } catch (error) {
      console.error('Error fetching group members:', error);
      setTargetGroupMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  const handleTargetGroupChange = (groupId: string) => {
    setTargetGroupId(groupId);
    setAssignedToId('');
    setTargetGroupMembers([]);
    if (groupId) {
      fetchGroupMembers(groupId);
    }
  };

  const handleReassignAndDelete = async () => {
    if (!groupToDelete || !targetGroupId) {
      toast.error('Please select a target group for reassignment');
      return;
    }

    setReassigning(true);
    try {
      // First reassign all items
      await api.post(`/groups/${groupToDelete.id}/reassign`, {
        targetGroupId,
        assignedToId: assignedToId || null
      });

      // Then delete the group
      await api.delete(`/groups/${groupToDelete.id}`);

      toast.success('Tickets reassigned and group deleted successfully');
      setReassignModalOpen(false);
      setGroupToDelete(null);
      setAssignments(null);
      setTargetGroupId('');
      setAssignedToId('');
      setTargetGroupMembers([]);
      fetchGroups();
    } catch (error: any) {
      console.error('Error reassigning and deleting group:', error);
      toast.error(error.response?.data?.message || 'Failed to reassign and delete group');
    } finally {
      setReassigning(false);
    }
  };

  const closeReassignModal = () => {
    setReassignModalOpen(false);
    setGroupToDelete(null);
    setAssignments(null);
    setTargetGroupId('');
    setAssignedToId('');
    setTargetGroupMembers([]);
  };

  const getTypeBadgeColor = (type: string) => {
    switch (type) {
      case 'assignment':
        return 'bg-indigo-100 text-indigo-800';
      case 'approval':
        return 'bg-pink-100 text-pink-800';
      case 'notification':
        return 'bg-cyan-100 text-cyan-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Show loading while permissions are being fetched
  if (permissionsLoading) {
    return (
      <div className="p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-sm text-gray-600">Loading permissions...</p>
        </div>
      </div>
    );
  }

  if (!canAccessGroups) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-gray-900">Access Denied</h1>
          <p className="mt-2 text-sm text-gray-700">
            You do not have permission to access groups.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 flex flex-col h-full min-h-0 overflow-hidden">
      <div className="bg-white shadow rounded-lg mb-2 flex-shrink-0">
        <div className="px-4 py-2">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Groups</h1>
              <p className="text-gray-600 mt-1">Manage assignment groups and teams</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="search"
                  placeholder="Search groups..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="pl-8 pr-3 py-1.5 w-64 text-sm border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  autoComplete="off"
                />
              </div>
              {canCreateGroups && (
                <Link
                  to="/groups/new"
                  state={{ groupsListSearch: location.search }}
                  className="inline-flex items-center px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                >
                  <PlusIcon className="h-4 w-4 mr-1.5" />
                  New Group
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {activeFilterChips.length > 0 && (
        <div className="bg-white shadow rounded-lg mb-2 px-4 py-2 flex-shrink-0">
          <ActiveFilterChips
            chips={activeFilterChips}
            onRemove={handleRemoveFilterChip}
            onClearAll={handleClearAllFilters}
          />
        </div>
      )}

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      ) : filteredGroups.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No groups found</h3>
          <p className="text-gray-500">Get started by creating a new group.</p>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden flex-1 min-h-0 flex flex-col">
          <div className="overflow-scroll flex-1" style={{ scrollbarGutter: 'stable' }}>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    {renderColumnHeader('name', 'Name')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    {renderColumnHeader('type', 'Type')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    Work Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    Default Hourly Rate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    {renderColumnHeader('manager', 'Manager')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    Members
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    {renderColumnHeader('status', 'Status')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    Actions
                  </th>
                </tr>
              </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedGroups.map((group) => (
                <tr key={group.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {group.name}
                      </div>
                      {group.description && (
                        <div className="text-sm text-gray-500">{group.description}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeBadgeColor(group.type)}`}>
                      {group.type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getGroupWorkLocationBadgeClass(group.workLocation)}`}
                    >
                      {formatGroupWorkLocation(group.workLocation)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {editingRateId === group.id ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          value={editingRateValue}
                          onChange={(e) => setEditingRateValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveRate(group.id);
                            if (e.key === 'Escape') handleCancelEditRate();
                          }}
                          className="w-24 px-2 py-1 border rounded focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="0.00"
                          step="0.01"
                          min="0"
                          autoFocus
                          disabled={savingRateId === group.id}
                        />
                        <button
                          onClick={() => handleSaveRate(group.id)}
                          disabled={savingRateId === group.id}
                          className="p-1 text-green-600 hover:text-green-800 disabled:opacity-50"
                          title="Save"
                        >
                          <CheckIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={handleCancelEditRate}
                          disabled={savingRateId === group.id}
                          className="p-1 text-gray-600 hover:text-gray-800 disabled:opacity-50"
                          title="Cancel"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="font-medium">
                          ${group.defaultHourlyRate ? Number(group.defaultHourlyRate).toFixed(2) : '0.00'}
                        </span>
                        {canEditGroups && (
                          <button
                            onClick={() => handleStartEditRate(group.id, group.defaultHourlyRate || 0)}
                            className="p-1 text-indigo-600 hover:text-indigo-800"
                            title="Edit rate"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {group.manager ? (
                      <div>
                        {group.manager.firstName} {group.manager.lastName}
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <UserGroupIcon className="h-4 w-4 text-gray-400 mr-1" />
                      {group.members?.length || group._count?.members || 0}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      group.isActive 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {group.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {canEditGroups && (
                      <Link
                        to={`/groups/${group.id}`}
                        state={{ groupsListSearch: location.search }}
                        className="text-indigo-600 hover:text-indigo-900 mr-3"
                      >
                        <PencilIcon className="h-5 w-5 inline" />
                      </Link>
                    )}
                    {canDeleteGroups && (
                      <button
                        onClick={() => handleDelete(group.id, group.name)}
                        disabled={checkingAssignments && groupToDelete?.id === group.id}
                        className="text-red-600 hover:text-red-900 disabled:opacity-50"
                      >
                        {checkingAssignments && groupToDelete?.id === group.id ? (
                          <div className="h-5 w-5 inline-block animate-spin rounded-full border-2 border-red-600 border-t-transparent" />
                        ) : (
                          <TrashIcon className="h-5 w-5 inline" />
                        )}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>

          {/* Pagination */}
          <div className="px-4 py-3 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-2 flex-shrink-0">
            <>
            {/* Left: Records per page dropdown */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-700">Show</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  patchSearchParams({
                    pageSize: e.target.value,
                    page: '1',
                  });
                }}
                className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
              <span className="text-sm text-gray-700">entries</span>
            </div>

            {/* Center: Entry count */}
            <div className="text-sm text-gray-700">
              {filteredGroups.length > 0
                ? `Showing ${(page - 1) * pageSize + 1} to ${Math.min(page * pageSize, filteredGroups.length)} of ${filteredGroups.length} entries`
                : 'Showing 0 of 0 entries'}
            </div>

            {/* Right: Page controls */}
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => patchSearchParams({ page: '1' })}
                disabled={page === 1 || filteredGroups.length === 0}
                className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="First page"
              >
                &laquo;
              </button>
              <button
                type="button"
                onClick={() => patchSearchParams({ page: String(page - 1) })}
                disabled={page === 1 || filteredGroups.length === 0}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              {(() => {
                const pages: (number | string)[] = [];
                if (totalPages <= 7) {
                  for (let i = 1; i <= totalPages; i++) pages.push(i);
                } else if (page <= 3) {
                  pages.push(1, 2, 3, 4, '...', totalPages);
                } else if (page >= totalPages - 2) {
                  pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
                } else {
                  pages.push(1, '...', page - 1, page, page + 1, '...', totalPages);
                }
                return pages.map((p, idx) =>
                  p === '...' ? (
                    <span key={`ellipsis-${idx}`} className="px-2 py-1 text-sm text-gray-500">
                      ...
                    </span>
                  ) : (
                    <button
                      key={p}
                      type="button"
                      onClick={() => patchSearchParams({ page: String(p) })}
                      className={`px-3 py-1 text-sm border rounded-md ${
                        page === p
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {p}
                    </button>
                  )
                );
              })()}
              <button
                type="button"
                onClick={() => patchSearchParams({ page: String(page + 1) })}
                disabled={page >= totalPages || filteredGroups.length === 0}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
              <button
                type="button"
                onClick={() => patchSearchParams({ page: String(totalPages) })}
                disabled={page >= totalPages || filteredGroups.length === 0}
                className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Last page"
              >
                &raquo;
              </button>
            </div>
            </>
          </div>
        </div>
      )}

      {/* Reassignment Modal */}
      {reassignModalOpen && assignments && groupToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-8 max-w-2xl mx-4 border border-gray-100 w-full">
            <div className="flex items-start mb-6">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                  <ExclamationTriangleIcon className="h-6 w-6 text-amber-600" />
                </div>
              </div>
              <div className="ml-4 flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Reassign Items Before Deletion
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  The group <span className="font-semibold">"{groupToDelete.name}"</span> has{' '}
                  <span className="font-semibold text-amber-600">{assignments.totalAssignments}</span> active
                  assignment(s). Please select a target group to reassign these items before deleting.
                </p>
              </div>
            </div>

            {/* Tickets Assigned Section */}
            {(assignments.tickets.incidents > 0 ||
              assignments.tickets.problems > 0 ||
              assignments.tickets.changes > 0 ||
              assignments.tickets.serviceRequests > 0 ||
              assignments.tickets.cases > 0) && (
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <h4 className="text-sm font-semibold text-gray-800 mb-3">Tickets Assigned</h4>
                <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
                  {assignments.tickets.incidents > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Incidents:</span>
                      <span className="font-medium">{assignments.tickets.incidents}</span>
                    </div>
                  )}
                  {assignments.tickets.problems > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Problems:</span>
                      <span className="font-medium">{assignments.tickets.problems}</span>
                    </div>
                  )}
                  {assignments.tickets.changes > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Changes:</span>
                      <span className="font-medium">{assignments.tickets.changes}</span>
                    </div>
                  )}
                  {assignments.tickets.serviceRequests > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Service Requests:</span>
                      <span className="font-medium">{assignments.tickets.serviceRequests}</span>
                    </div>
                  )}
                  {assignments.tickets.cases > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Cases:</span>
                      <span className="font-medium">{assignments.tickets.cases}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Groups allocated to Section */}
            {assignments.engagements.count > 0 && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h4 className="text-sm font-semibold text-gray-800 mb-3">Groups allocated to:</h4>
                <div className="text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Engagements:</span>
                    <span className="font-medium">{assignments.engagements.count}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Target group selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reassign to Group <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center gap-3">
                <span className="text-gray-500 text-sm">{groupToDelete.name}</span>
                <ArrowRightIcon className="h-4 w-4 text-gray-400" />
                <select
                  value={targetGroupId}
                  onChange={(e) => handleTargetGroupChange(e.target.value)}
                  className="flex-1 px-3 py-2 border rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  disabled={reassigning}
                >
                  <option value="">Select a target group...</option>
                  {groups
                    .filter(g => g.id !== groupToDelete.id && g.isActive)
                    .map(g => (
                      <option key={g.id} value={g.id}>
                        {g.name} ({g.type})
                      </option>
                    ))}
                </select>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                All active tickets and assignments will be transferred to the selected group.
              </p>
            </div>

            {/* Assigned To selection - only show when target group is selected */}
            {targetGroupId && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assigned To <span className="text-red-500">*</span>
                </label>
                <select
                  value={assignedToId}
                  onChange={(e) => setAssignedToId(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  disabled={reassigning || loadingMembers}
                >
                  <option value="">Select an assignee...</option>
                  {loadingMembers ? (
                    <option disabled>Loading members...</option>
                  ) : targetGroupMembers.length === 0 ? (
                    <option disabled>No active members in selected group</option>
                  ) : (
                    targetGroupMembers.map(member => (
                      <option key={member.id} value={member.id}>
                        {member.firstName} {member.lastName} ({member.email})
                      </option>
                    ))
                  )}
                </select>
                <p className="mt-2 text-xs text-gray-500">
                  All tickets will be assigned to this member of the target group.
                </p>
              </div>
            )}

            {/* Engagement warning note */}
            {assignments.engagements.count > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> Association of deleted group will be removed from Engagements.
                  Engagement agents cannot be automatically reassigned. You may need to manually update
                  engagement agent assignments after deletion.
                </p>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end space-y-2 space-y-reverse sm:space-y-0 sm:space-x-3">
              <button
                className="px-6 py-3 bg-gray-200 text-gray-900 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200 font-medium disabled:opacity-50"
                onClick={closeReassignModal}
                disabled={reassigning}
              >
                Cancel
              </button>
              <button
                className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                onClick={handleReassignAndDelete}
                disabled={!targetGroupId || !assignedToId || reassigning}
              >
                {reassigning ? (
                  <>
                    <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Reassigning...
                  </>
                ) : (
                  'Reassign & Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}